import { useEffect, useRef, useState, KeyboardEvent } from "react";
import { Row, Radio, DatePicker as AntDatePicker, Tag, InputRef } from "antd";
import { FilterDropdownProps } from "antd/lib/table/interface";
import moment from "moment";

import "moment/locale/fr";

import localeFr from "antd/lib/date-picker/locale/fr_FR";
import Search, { defaultPagination } from "../../../../model/search";
import { paginationLens, paramsLens } from "../../lenses";
import { remove } from "../../../../lib/array";
import { Maybe } from "../../../../interfaces/Utils";

const { Group } = Radio;
const { RangePicker } = AntDatePicker;

const EXACT_DATE = "EXACT_DATE";
const RANGE_DATE = "RANGE_DATE";
const DEFAULT_MODE = RANGE_DATE;
const modesOptions = (lang: string) => [
	{
		value: EXACT_DATE,
		label: (
			<span
				style={{
					width: 104,
					display: "flex",
					justifyContent: "center",
				}}
			>
				Date
			</span>
		),
	},
	{
		value: RANGE_DATE,
		label: (
			<span
				style={{
					width: 104,
					display: "flex",
					justifyContent: "center",
				}}
			>
				{lang === "en" ? "Cycle" : "Période"}
			</span>
		),
	},
];

const frFmt = "DD/MM/YYYY";

// TODO: Fill it properly
// const popover = {
// 	title: 'Comment ça marche ?',
// 	content: (<div>
// 		{Array
// 			.from({ length: 5 }, (_, i) => i)
// 			.map(i => <div key={_uniqueId("expl-")}>Explication {i}</div>)
// 		}
// 	</div>),
// };

// TODO: Move in a lib maybe ? Are a specialised display lib for the project
export function formattedMoment(v: any): Maybe<string> {
	if (moment.isMoment(v)) {
		return (v as moment.Moment).format(frFmt);
	}
	if (Array.isArray(v)) {
		const [beg, end] = v;
		const begS = moment.isMoment(beg) ? beg.format(frFmt) : undefined;
		const endS = moment.isMoment(end) ? end.format(frFmt) : undefined;
		if (!!begS && !!endS) return `Du ${begS} au ${endS}`;
		if (begS) return `À partir du ${begS}`;
		if (endS) return `Jusqu'au ${endS}`;
	}
	return undefined;
}

export const modes = Object.freeze({ EXACT_DATE: EXACT_DATE, RANGE_DATE: RANGE_DATE });

interface DatePickerProps extends FilterDropdownProps {
	e: any;
	search: Search;
	updateSearch(s: Search): void;
	lang: string;
}

function DatePicker(props: DatePickerProps) {
	const { search, updateSearch, e, lang } = props;
	const index = e?._dataIndex;
	const prevParams = paramsLens.get(search);
	const prevVal = prevParams[index];

	const [mode, setMode] = useState(DEFAULT_MODE);

	const options = {
		locale: lang === "en" ? undefined : localeFr,
		format: frFmt,
		value: null,
		style: { width: 256 },
		onChange: function (newVal: any) {
			const s = paramsLens.set(search, {
				...prevParams,
				[index]: [...(prevVal || []), newVal],
			});
			const s2 = paginationLens.set(s, defaultPagination);
			updateSearch(s2);
		},
		onKeyDown: function (evt: KeyboardEvent<HTMLInputElement>) {
			const { key } = evt;
			if (key !== "Enter") return;

			const parent = document.getElementsByClassName("inner-hacky-ref");
			const children = parent[0]?.children;
			const values: (moment.Moment | undefined)[] = [];
			for (let i = 0; i < children.length; i++) {
				const item = children.item(i);
				if (item?.className?.indexOf("ant-picker-input") === -1) continue;
				const child = item?.children.item(0);
				const value = child?.getAttribute("value") ?? "";
				values.push(value ? moment(value) : undefined);
			}

			const s = paramsLens.set(search, {
				...prevParams,
				[index]: [...(prevVal || []), values],
			});
			const s2 = paginationLens.set(s, defaultPagination);
			updateSearch(s2);
		},
		className: "inner-hacky-ref",
	};

	let picker;
	const inputRef = useRef<InputRef>(null);
	useEffect(() => {
		if (inputRef.current) {
			setTimeout(() => {
				// Sans le timeout l'input est pas encore dans le DOM du coup le focus ne fonctionne pas, pas trouvé mieux pour le moment
				inputRef.current.focus();
			}, 100);
		}
	});

	switch (mode) {
		case EXACT_DATE: {
			picker = <AntDatePicker ref={inputRef} autoFocus size="small" {...options} />;
			break;
		}
		case RANGE_DATE:
		default: {
			picker = <RangePicker ref={inputRef} autoFocus size="small" {...options} />;
		}
	}

	const tags = prevVal?.map((v, i) => {
		const onAction = (_: any): void => {
			updateSearch(
				paramsLens.set(search, {
					...prevParams,
					[index]: remove(prevVal, i),
				})
			);
		};
		const tagProps = {
			style: { cursor: "pointer", margin: 5 },
			onClick: onAction,
			onClose: onAction,
			closable: true,
		};
		const s = formattedMoment(v);
		return s ? <Tag {...tagProps}>{s}</Tag> : null;
	});

	const tagsWrapperStyle = {
		display: "flex" as const,
		flexDirection: "column" as const,
		alignContent: "center" as const,
		justifyContent: "center" as const,
	};

	return (
		<div style={{ width: 256, padding: 8 }}>
			<Row style={{ display: "flex", justifyContent: "space-between" }}>
				<Group
					options={modesOptions(lang)}
					onChange={({ target: { value } }) => setMode(value)}
					value={mode}
					optionType="button"
					buttonStyle="solid"
					size="small"
				/>
				{/* <Popover {...popover}>
					<QuestionCircleOutlined style={{ cursor: 'help', marginRight: 10 }} />
				</Popover> */}
			</Row>
			<Row style={{ marginTop: 8 }}>{picker}</Row>
			<Row style={{ marginTop: tags ? 8 : 0 }}>
				{tags ? <div style={tagsWrapperStyle}>{tags}</div> : null}
			</Row>
		</div>
	);
}

DatePicker.defaultProps = {
	lang: "fr",
} as DatePickerProps;

export default DatePicker;
